import React from 'react';
import './CarCalculatorInfoPage.css';

const TermsOfService: React.FC = () => {
  return (
    <div className="info-page">
      <div className="info-container">
        <h1>Terms of Service</h1>
        <p className="last-updated">Last Updated: February 24, 2026</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using Larry's Help ("Service"), you agree to be bound by these Terms of Service ("Terms").
            If you do not agree to these Terms, please do not use the Service. We reserve the right to modify
            these Terms at any time, and your continued use of the Service constitutes acceptance of any changes.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            Larry's Help provides financial planning calculators and educational tools, including but not limited to:
          </p>
          <ul>
            <li>Car comparison calculator</li>
            <li>Housing/rent vs buy calculator</li>
            <li>Career comparison calculator</li>
            <li>Retirement planning calculator (RRSP/TFSA for Canada, 401k/IRA for USA)</li>
            <li>Income tax calculator</li>
            <li>Payroll deduction calculator</li>
            <li>Personal financial forecast</li>
            <li>Scheduling and booking platform</li>
            <li>Bookkeeping and accounting tools</li>
          </ul>
          <p>
            These tools are provided for <strong>educational and informational purposes only</strong> and do not
            constitute financial, investment, tax, or legal advice.
          </p>
        </section>

        <section>
          <h2>3. User Accounts</h2>
          <h3>3.1 Account Creation</h3>
          <p>
            You may create an account using your email address or Google account. You are responsible for
            maintaining the confidentiality of your account credentials and for all activities that occur under your account.
          </p>
          <h3>3.2 Account Termination</h3>
          <p>
            We reserve the right to suspend or terminate accounts that violate these Terms or engage in
            fraudulent activity. You may delete your account at any time by contacting us.
          </p>
        </section>

        <section>
          <h2>4. Supporters Club Membership</h2>
          <h3>4.1 Pricing</h3>
          <p>
            The Supporters Club membership is $10 CAD per year. This is a one-time annual payment with no automatic renewal.
          </p>
          <h3>4.2 Benefits</h3>
          <p>
            Supporters Club members receive unlimited saved scenarios, advanced calculator features, bookkeeping for up to 10 businesses, scheduling Pro features, and priority support.
          </p>
          <h3>4.3 Refund Policy</h3>
          <p>
            Due to the low cost and immediate access to all features, <strong>refunds are not available</strong>.
            The Supporters Club costs less than a coffee per month and helps us maintain free access for everyone.
          </p>
          <h3>4.4 Price Changes</h3>
          <p>
            If we increase prices in the future, existing members will be grandfathered at their original rate.
          </p>
        </section>

        <section>
          <h2>5. Scheduling Platform Terms</h2>
          <h3>5.1 Booking and Cancellation</h3>
          <p>
            When you use our scheduling platform to create booking links or schedule meetings, you are responsible for
            managing your availability and honoring scheduled appointments. Invitees who book meetings with you consent
            to sharing their contact information (name, email) with you and with Larry's Help to facilitate the booking.
          </p>
          <h3>5.2 Payment Processing</h3>
          <p>
            If you accept payments through the scheduling platform, payments are processed via Stripe Connect.
            Larry's Help may charge a platform fee on paid bookings. You are responsible for any refunds or disputes
            with your invitees.
          </p>
          <h3>5.3 Calendar Integration</h3>
          <p>
            If you connect Google Calendar or Microsoft Outlook, we access your calendar solely to check availability
            and create events. You can revoke calendar access at any time through your calendar provider's settings.
          </p>
        </section>

        <section>
          <h2>6. Accounting and Bookkeeping Terms</h2>
          <h3>6.1 Not Professional Advice</h3>
          <p>
            The accounting and bookkeeping tools provided by Larry's Help are <strong>not a substitute for professional
            accounting, tax, or legal advice</strong>. You should consult a qualified CPA, accountant, or tax professional
            before filing taxes or making financial decisions based on data from our tools.
          </p>
          <h3>6.2 Data Accuracy</h3>
          <p>
            You are solely responsible for the accuracy of your financial records, journal entries, and transaction data
            entered into our accounting tools. Larry's Help does not verify, audit, or guarantee the correctness of your
            bookkeeping data.
          </p>
          <h3>6.3 Record Retention</h3>
          <p>
            Accounting records are retained for a minimum of 7 years to comply with Canada Revenue Agency (CRA)
            record-keeping requirements, even if your account is closed. You may export your data at any time.
          </p>
          <h3>6.4 Audit Logging</h3>
          <p>
            All accounting actions (creating, posting, and voiding journal entries; account changes) are logged for
            security and compliance purposes. Audit logs include the action performed, timestamp, user identity,
            and IP address.
          </p>
        </section>

        <section>
          <h2>7. Disclaimer of Warranties</h2>
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
          </p>
          <ul>
            <li><strong>No Financial Advice:</strong> The calculators and tools are not a substitute for professional financial, tax, or legal advice.</li>
            <li><strong>No Accuracy Guarantee:</strong> While we strive for accuracy, calculations are estimates based on assumptions and may not reflect your actual situation.</li>
            <li><strong>No Liability:</strong> Larry's Help is not liable for any decisions made based on information from our tools.</li>
          </ul>
        </section>

        <section>
          <h2>8. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, LARRY'S HELP SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL,
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR
            OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF THE SERVICE.
          </p>
          <p>
            You acknowledge that financial decisions involve risk, and you are solely responsible for verifying
            all calculations and seeking professional advice before making financial decisions.
          </p>
        </section>

        <section>
          <h2>9. User Responsibilities</h2>
          <p>You agree to:</p>
          <ul>
            <li>Provide accurate information when using the calculators</li>
            <li>Not use the Service for any unlawful purpose</li>
            <li>Not attempt to reverse engineer, hack, or disrupt the Service</li>
            <li>Not create multiple accounts to circumvent scenario limits</li>
            <li>Verify calculations with a qualified professional before making financial decisions</li>
          </ul>
        </section>

        <section>
          <h2>10. Intellectual Property</h2>
          <p>
            All content, features, and functionality of the Service, including but not limited to text, graphics,
            logos, and software, are the property of Larry's Help and are protected by copyright and other
            intellectual property laws.
          </p>
        </section>

        <section>
          <h2>11. Third-Party Services</h2>
          <p>
            The Service may integrate with third-party services including:
          </p>
          <ul>
            <li><strong>Stripe:</strong> For payment processing</li>
            <li><strong>Google:</strong> For authentication and analytics</li>
            <li><strong>Google AdSense:</strong> For advertising to non-subscribers</li>
          </ul>
          <p>
            Your use of these third-party services is subject to their respective terms and privacy policies.
          </p>
        </section>

        <section>
          <h2>12. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the Province of Ontario, Canada,
            without regard to its conflict of law provisions. Any disputes arising from these Terms shall be
            resolved in the courts of Ontario, Canada.
          </p>
        </section>

        <section>
          <h2>13. Contact Information</h2>
          <p>
            For questions about these Terms of Service, please contact us at:
          </p>
          <ul>
            <li><strong>Email:</strong> support@larryshelp.org</li>
            <li><strong>Website:</strong> https://larryshelp.org</li>
          </ul>
        </section>

        <section>
          <h2>14. Severability</h2>
          <p>
            If any provision of these Terms is found to be unenforceable or invalid, that provision shall be
            limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain
            in full force and effect.
          </p>
        </section>

      </div>
    </div>
  );
};

export default TermsOfService;
